import './nav-menu.css';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { userLoaded, tourStarted } from '../../redux/actions';
import { useNavigate } from 'react-router-dom';
import { serverFetch } from '../../server';
import NavMenuItem from './nav-menu-item';
import { message } from 'antd';
import { isMobile } from '../../extensions/design';
import { licenseModuleBase, licenseModuleMessages } from '@/extensions';
import { subscribeCompanyRefresh } from '@/services/companyRefreshService';

const DEFAULT_LOGO_URL = '/images/logo.svg';
const COMPANY_LOGO_CACHE_KEY = 'companyLogoFileName';

function getLogoUrl(fileName) {
    return fileName
        ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${fileName}`
        : DEFAULT_LOGO_URL;
}

function readCachedLogoUrl() {
    try {
        const cached = localStorage.getItem(COMPANY_LOGO_CACHE_KEY);
        if (cached === null) {
            return null;
        }
        return getLogoUrl(cached);
    } catch {
        return null;
    }
}

function writeCachedLogoFileName(fileName) {
    try {
        localStorage.setItem(COMPANY_LOGO_CACHE_KEY, fileName ?? '');
    } catch {
        // ignore quota / private mode
    }
}

const NavMenu = () => {
    const d = useDispatch();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [company, setCompany] = useState();
    const [logoUrl, setLogoUrl] = useState(readCachedLogoUrl);
    const user = useSelector((s) => s.user);

    const activeLicenseModules = company?.activeLicense?.activeModules;

    const loadCompany = useCallback(() => {
        serverFetch(`company/current`)
            .then(data => {
                setCompany(data);
                const fileName = data?.pictureFileName ?? '';
                writeCachedLogoFileName(fileName);
                setLogoUrl(getLogoUrl(fileName));
            })
            .catch(e => message.error(e.userMessage));
    }, []);

    const logout = () => {
        serverFetch(`auth/logout`, { method: 'POST' })
            .then(() => {
                d(userLoaded());
            })
            .catch(() => {
                message.error('Ошибка выхода из системы');
            });
    };

    const startTour = useCallback(() => {
        d(tourStarted());
        navigate('/');
    }, [d, navigate]);    

    const logoutTooltip = user ? <div>
        <div>Выход из системы</div>
        <div>{user.userName}</div>
        <div>{user.email}</div>
    </div> : null;

    useEffect(() => {
      const newItems = [];
      if (isMobile) {
        licenseModuleBase(activeLicenseModules) && user.companyId && newItems.push(
            getItem('Визиты', 'visits', '/visits', 'sale.svg', null, false, null, 'visits'),
            getItem('Запись', 'records', '/records', 'calendar.svg', null, false, null, 'records'),
            getItem('Клиенты', 'clients', '/clients', 'person.svg', null, false, null, 'clients'),
            
        );
        licenseModuleMessages(activeLicenseModules) && user.companyId && newItems.push(
            getItem('Диалоги', 'messages', '/dialogs', 'mail.svg', null, false, null, 'messages', false, company?.dialogUnreadQty ?? 0),
        );
        newItems.push(getItem('Выход', 'logout', '', 'exit.svg', logout),);
      }
      else 
      {
        licenseModuleBase(activeLicenseModules) && user.companyId && newItems.push(
            getItem('Визиты', 'visits', '/visits', 'sale.svg', null, false, null, 'visits'),
            getItem('Запись', 'records', '/records', 'calendar.svg', null, false, null, 'records'),
            getItem('Клиенты', 'clients', '/clients', 'person.svg', null, false, null, 'clients'),
        );
        licenseModuleMessages(activeLicenseModules) && user.companyId && newItems.push(
            getItem('Диалоги', 'messages', '/dialogs', 'mail.svg', null, false, null, 'messages', false, company?.dialogUnreadQty ?? 0),
        );
        licenseModuleBase(activeLicenseModules) && user.companyId && newItems.push && company.userRoles !== 20 && newItems.push (
            getItem('Склад', 'stock', '/stock', 'stock.svg', null, false, null, 'stock'),
        );
        licenseModuleBase(activeLicenseModules) && user.companyId && newItems.push(
            getItem('Журналы', 'journals', '', 'card.svg', null, false, null, 'journals', false, 0, [
                getItem('Оплаты', 'journal-payments', '/payments'),
                getItem('Продажи', 'journal-sales', '/sales'),
                user.isAdmin && getItem('Реестры', 'journal-reestrs', '/reestrs'),
                licenseModuleBase(activeLicenseModules) && user.isAdmin && getItem('Монитор', 'journal-logs', '/logs'),
            ]),
        );
        licenseModuleBase(activeLicenseModules) && user.companyId && newItems.push(
            getItem('Отчеты', 'reports', '/reports', 'print.svg', null, false, null, 'reports'),
        );
        licenseModuleBase(activeLicenseModules) && user.companyId && newItems.push(
            getItem('Справочники', 'dicts', '', 'settings.svg',  null, false, null, 'dicts', false, 0, [
                getItem('Номенклатура', 'dict-products', '/settings/products', null, null, false, null, 'dict-products'),
                getItem('Виды оплаты', 'dict-paymentvids', '/settings/paymentvids', null, null, false, null, 'dict-paymentvids'),
                getItem('Залы', 'dict-rooms', '/settings/rooms', null, null, false, null, 'dict-rooms'),
                getItem('Карты', 'dict-cards', '/settings/cards', null, null, false, null, 'dict-cards'),
                getItem('Теги', 'dict-tags', '/settings/tags'),
                getItem('Реквизиты', 'dict-requisites', '/settings/requisites'),
            ]),
        );
        user.isAdmin && user.companyId && newItems.push(
            getItem('Лицензии', 'license', '/license', 'key.svg', null, false, null, 'licenses'),
        );
        user.companyId && newItems.push(getItem('Настройки', 'settings', '/settings/company', user.isAdmin ? 'settings.svg' : 'settings-disabled.svg', null, false, null, 'settings'));
        licenseModuleBase(activeLicenseModules) && user.isAdmin && user.companyId && newItems.push(getItem('Сервис', 'service', '/service', user.isAdmin ? 'service.svg' : 'service-disabled.svg', null, !user.isAdmin, null, 'service'));
        user.isSuperadmin && newItems.push(getItem('Компании', 'companies', '/companies', 'monitor.svg', null, false, null, 'companies'));
        newItems.push(getItem('Помощь', 'help', '', 'help.svg', null, false, null, 'help', false, 0, [
            getItem('База знаний', 'help-kb', 'https://kb.sport24.cloud/', null, null, false, null, 'help-kb', true),
            licenseModuleBase(activeLicenseModules) && getItem('Тур', 'help-tour', '', null, startTour, false, 'Запустить экскурсию по всем страницам', 'tour'),
        ]));
        newItems.push(getItem('Выход', 'logout', '', 'exit.svg', logout, false, logoutTooltip, 'exit'));
      }
      setItems(newItems)
      // eslint-disable-next-line
    }, [user, startTour, company]);

    useEffect(() => {
        loadCompany();
    }, [loadCompany])

    useEffect(() => subscribeCompanyRefresh(loadCompany), [loadCompany])

    function getItem(label, key, urlTo, img, onClick, disabled = false, tooltip = null, id = '', external = false, badgeCount = 0, children = null) {
        return { key, label, url: urlTo, img, onClick, disabled, tooltip, id, external, badgeCount, children };
    }

    const logoWidth = isMobile ? 32 : 80;

    return (
        <>
            <div className='nav-menu-container' data-tour-target="nav-menu">
                <div className='sider-logo-new' id={`tour-item-nav-menu-dashboard`}>
                    <a href='/'>
                        {logoUrl
                            ? <img src={logoUrl} alt="logo" style={{ width: logoWidth, height: 'auto', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
                            : <span style={{ display: 'block', width: logoWidth, height: logoWidth }} />
                        }
                    </a>
                </div>
                {items.map(i => <NavMenuItem {...i} key={i.key} itemId={`tour-item-nav-menu-${i.id}`} />)}
                {/* <div className='nav-menu-footer'>(с) Компания Проффит 2010 - 2025</div> */}
                <div className='nav-menu-footer'>{process.env.REACT_APP_VERSION}</div>
            </div>
        </>
    );
};

export default NavMenu;
